// One read, four states, and the reason the fourth one exists.
//
// Every page this phase adds fetches once and then draws. All four of loading,
// loaded, empty and refused have to render honestly — an account that has
// bought nothing must say so rather than showing a blank card, and a refusal
// must show what the service said rather than an empty table that looks like
// "you have none".
//
// A hook rather than a component so the pages keep their own layout, and in a
// .ts file rather than a .tsx one because the repo lints with
// `react-refresh/only-export-components`.

import { useCallback, useEffect, useState } from 'react';

import { PortalDataFailed } from './subscriptions';

export type Loaded<T> =
  | { status: 'loading' }
  | { status: 'loaded'; data: T }
  | { status: 'refused'; refusal: string };

/**
 * Run one read on mount, and again whenever `reload` is called.
 *
 * @param read Fetches the data. Must be stable — wrap it in `useCallback`, or
 *   the effect re-runs on every render.
 */
export function useLoaded<T>(read: () => Promise<T>): {
  state: Loaded<T>;
  reload: () => void;
} {
  const [state, setState] = useState<Loaded<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;

    (async () => {
      try {
        const data = await read();
        if (current) {
          setState({ status: 'loaded', data });
        }
      } catch (failure) {
        if (!current) {
          return;
        }
        if (failure instanceof PortalDataFailed || failure instanceof Error) {
          setState({ status: 'refused', refusal: failure.message });
          return;
        }
        throw failure;
      }
    })();

    return () => {
      current = false;
    };
  }, [read, attempt]);

  const reload = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((previous) => previous + 1);
  }, []);

  return { state, reload };
}
